import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { fetchAllPages } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import SearchSelect from '@/components/common/SearchSelect';
import { PARTICIPANT_SEARCH_HINT } from '@/components/common/pickerLabels';
import { toast } from 'sonner';
import { Users, Eye, DoorOpen, UserPlus, History, Calendar } from 'lucide-react';

export default function HouseResidentsTab({ house, houseId }) {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [allocations, setAllocations] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showMoveOutDialog, setShowMoveOutDialog] = useState(false);
  const [selectedAllocation, setSelectedAllocation] = useState(null);
  const [assignForm, setAssignForm] = useState({ participant_id: '', room_id: '', move_in_date: '', notes: '' });
  const [moveOutForm, setMoveOutForm] = useState({ move_out_date: '', reason: '' });

  useEffect(() => {
    fetchData();
  }, [houseId]);

  const fetchData = async () => {
    try {
      const [allocationsRes, roomsRes, participantsRes] = await Promise.all([
        api.get(`/accommodation/by-house/${houseId}`),
        api.get(`/houses/${houseId}/rooms`),
        fetchAllPages('/participants')   // #48: `page_size=200` was ignored (alias is pageSize, max 100) → only 20 people listed
      ]);
      setAllocations(allocationsRes.data.data || []);
      setRooms(roomsRes.data.data || []);
      setParticipants(participantsRes || []);
    } catch (error) {
      toast.error('Failed to load residents');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignResident = async (e) => {
    e.preventDefault();
    try {
      await api.post('/accommodation/assign', {
        participant_id: assignForm.participant_id,
        house_id: houseId,
        room_id: assignForm.room_id,
        move_in_date: assignForm.move_in_date,
        notes: assignForm.notes
      });
      toast.success('Resident assigned successfully');
      setShowAssignDialog(false);
      setAssignForm({ participant_id: '', room_id: '', move_in_date: '', notes: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail?.message || 'Failed to assign resident');
    }
  };

  const handleMoveOut = async (e) => {
    e.preventDefault();
    try {
      await api.post('/accommodation/move-out', {
        allocation_id: selectedAllocation.id,
        move_out_date: moveOutForm.move_out_date,
        reason: moveOutForm.reason
      });
      toast.success('Resident moved out successfully');
      setShowMoveOutDialog(false);
      setMoveOutForm({ move_out_date: '', reason: '' });
      setSelectedAllocation(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail?.message || 'Failed to move out resident');
    }
  };

  const openMoveOutDialog = (allocation) => {
    setSelectedAllocation(allocation);
    setMoveOutForm({ move_out_date: new Date().toISOString().split('T')[0], reason: '' });
    setShowMoveOutDialog(true);
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;
  }

  const activeAllocations = allocations.filter(a => a.status === 'ACTIVE');
  const pastAllocations = allocations.filter(a => a.status !== 'ACTIVE');
  const availableRooms = rooms.filter(r => r.status === 'AVAILABLE');
  
  // Filter out participants already in this house
  const assignedParticipantIds = activeAllocations.map(a => a.participant_id);
  const availableParticipants = participants.filter(p => !assignedParticipantIds.includes(p.id));

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="font-semibold">House Residents</h3>
          <p className="text-sm text-muted-foreground">
            {activeAllocations.length} of {house.capacity} capacity ({availableRooms.length} rooms available)
          </p>
        </div>
        {isAdmin && (
          <Button onClick={() => setShowAssignDialog(true)} disabled={availableRooms.length === 0} data-testid="assign-resident-btn">
            <UserPlus className="h-4 w-4 mr-2" /> Assign Resident
          </Button>
        )}
      </div>

      <Tabs defaultValue="current">
        <TabsList>
          <TabsTrigger value="current" className="gap-2">
            <Users className="h-4 w-4" /> Current ({activeAllocations.length})
          </TabsTrigger>
          <TabsTrigger value="history" className="gap-2">
            <History className="h-4 w-4" /> History ({pastAllocations.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="current">
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Participant</TableHead>
                  <TableHead>Room</TableHead>
                  <TableHead>Move In Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {activeAllocations.map((allocation) => (
                  <TableRow key={allocation.id} data-testid={`resident-row-${allocation.id}`}>
                    <TableCell className="font-medium">{allocation.participant_name || 'N/A'}</TableCell>
                    <TableCell>{allocation.room_name || 'N/A'}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {allocation.move_in_date ? new Date(allocation.move_in_date).toLocaleDateString() : 'N/A'}
                    </TableCell>
                    <TableCell>
                      <Badge variant="default" className="bg-green-100 text-green-700">{allocation.status}</Badge>
                    </TableCell>
                    <TableCell className="text-right space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/participants/${allocation.participant_id}`)}
                        data-testid={`view-resident-${allocation.id}`}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </Button>
                      {isAdmin && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openMoveOutDialog(allocation)}
                          data-testid={`moveout-resident-${allocation.id}`}
                        >
                          <DoorOpen className="h-4 w-4 mr-1" />
                          Move Out
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {activeAllocations.length === 0 && (
              <div className="py-12 text-center text-muted-foreground">
                <Users className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No residents currently assigned</p>
                {isAdmin && (
                  <Button variant="outline" className="mt-4" onClick={() => setShowAssignDialog(true)}>
                    <UserPlus className="h-4 w-4 mr-2" /> Assign First Resident
                  </Button>
                )}
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="history">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Occupancy History</CardTitle>
            </CardHeader>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Participant</TableHead>
                  <TableHead>Room</TableHead>
                  <TableHead>Move In</TableHead>
                  <TableHead>Move Out</TableHead>
                  <TableHead>Duration</TableHead>
                  <TableHead>Reason</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pastAllocations.map((allocation) => {
                  const moveIn = allocation.move_in_date ? new Date(allocation.move_in_date) : null;
                  const moveOut = allocation.move_out_date ? new Date(allocation.move_out_date) : null;
                  const duration = moveIn && moveOut ? Math.ceil((moveOut - moveIn) / (1000 * 60 * 60 * 24)) : null;
                  
                  return (
                    <TableRow key={allocation.id} className="text-muted-foreground">
                      <TableCell className="font-medium">{allocation.participant_name || 'N/A'}</TableCell>
                      <TableCell>{allocation.room_name || 'N/A'}</TableCell>
                      <TableCell className="text-sm">
                        {moveIn ? moveIn.toLocaleDateString() : 'N/A'}
                      </TableCell>
                      <TableCell className="text-sm">
                        {moveOut ? moveOut.toLocaleDateString() : 'N/A'}
                      </TableCell>
                      <TableCell className="text-sm">
                        {duration ? `${duration} days` : 'N/A'}
                      </TableCell>
                      <TableCell className="text-sm">
                        <Badge variant="outline" className="text-xs">{allocation.status}</Badge>
                        {allocation.move_out_reason && (
                          <span className="ml-2 text-xs">{allocation.move_out_reason}</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            {pastAllocations.length === 0 && (
              <CardContent>
                <div className="py-8 text-center text-muted-foreground">
                  <History className="h-10 w-10 mx-auto mb-2 opacity-50" />
                  <p>No occupancy history</p>
                </div>
              </CardContent>
            )}
          </Card>
        </TabsContent>
      </Tabs>

      {/* Assign Resident Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent>
          <form onSubmit={handleAssignResident}>
            <DialogHeader>
              <DialogTitle>Assign Resident</DialogTitle>
              <DialogDescription>Add a participant to this house</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Participant *</Label>
                <SearchSelect
                  options={availableParticipants.map(p => ({ value: p.id, label: `${p.first_name || ''} ${p.last_name || ''} (${p.ndis_number || 'No NDIS'})` }))}
                  value={assignForm.participant_id}
                  onChange={(v) => setAssignForm({...assignForm, participant_id: v})}
                  placeholder="Select a participant..."
                  searchPlaceholder={PARTICIPANT_SEARCH_HINT}
                  emptyText="No participants match that search"
                  testId="select-participant"
                />
              </div>
              <div className="space-y-2">
                <Label>Room *</Label>
                <Select value={assignForm.room_id} onValueChange={(v) => setAssignForm({...assignForm, room_id: v})}>
                  <SelectTrigger data-testid="select-room">
                    <SelectValue placeholder="Select a room..." />
                  </SelectTrigger>
                  <SelectContent>
                    {availableRooms.map(r => (
                      <SelectItem key={r.id} value={r.id}>
                        {r.name} (Capacity: {r.capacity})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Move In Date *</Label>
                <Input 
                  type="date" 
                  value={assignForm.move_in_date} 
                  onChange={(e) => setAssignForm({...assignForm, move_in_date: e.target.value})}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea 
                  value={assignForm.notes} 
                  onChange={(e) => setAssignForm({...assignForm, notes: e.target.value})}
                  placeholder="Any special arrangements or notes..."
                  rows={2}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAssignDialog(false)}>Cancel</Button>
              <Button type="submit" disabled={!assignForm.participant_id || !assignForm.room_id || !assignForm.move_in_date}>
                Assign Resident
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Move Out Dialog */}
      <Dialog open={showMoveOutDialog} onOpenChange={setShowMoveOutDialog}>
        <DialogContent>
          <form onSubmit={handleMoveOut}>
            <DialogHeader>
              <DialogTitle>Move Out Resident</DialogTitle>
              <DialogDescription>
                {selectedAllocation?.participant_name} from {selectedAllocation?.room_name}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Move Out Date *</Label>
                <Input 
                  type="date" 
                  value={moveOutForm.move_out_date} 
                  onChange={(e) => setMoveOutForm({...moveOutForm, move_out_date: e.target.value})}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Reason</Label>
                <Textarea 
                  value={moveOutForm.reason} 
                  onChange={(e) => setMoveOutForm({...moveOutForm, reason: e.target.value})}
                  placeholder="Reason for move out..."
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowMoveOutDialog(false)}>Cancel</Button>
              <Button type="submit" variant="destructive">Confirm Move Out</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
